import React from 'react';
import { FileCheck, RefreshCw, User, LogOut, CheckCircle, AlertTriangle, ArrowRight, XCircle, X, MinusCircle } from 'lucide-react';
import DocumentPreview from './DocumentPreview';
import OrderHeader from './OrderHeader';
import ShipToCard from './ShipToCard';
import LineItems from './LineItems';
import OrderComments from './OrderComments';
import OrderTotals from './OrderTotals';
import ValidationIssues from './ValidationIssues';
import CustomerShipToSearchModal from './CustomerShipToSearchModal';

export default function ReviewStage({
  user,
  extractedData,
  validationResult,
  selectedFile,
  filePreviewUrl,
  previewType,
  textContent,
  zoom,
  selectedCustomer,
  selectedShipTo,
  selectedItems,
  itemsRevalidating,
  itemRevalidationFailed,
  requestedShipDate,
  onRequestedShipDateChange,
  fobCodes,
  carrierCodes,
  selectedFobCode,
  selectedCarrierCode,
  onFobCodeChange,
  onCarrierCodeChange,
  warehouseCodes,
  selectedWarehouse,
  onWarehouseChange,
  inventoryLoading,
  shipVia,
  onShipViaChange,
  customerDropdownOpen,
  shipToDropdownOpen,
  unresolvedCount,
  unresolvedIssues,
  bulkPrice,
  onZoomIn,
  onZoomOut,
  onCustomerDropdownToggle,
  onSelectCustomer,
  onShipToDropdownToggle,
  onSelectShipTo,
  salesRep,
  correlationId,
  onUpdateItemSelection,
  onConfirmItem,
  onUpdateItemQuantity,
  onValidatePrice,
  onResolvePrice,
  onDeleteLineItem,
  onUpdateNote,
  onUpdateNotePrintCode,
  onUpdateItemVendor,
  onUpdateItemCost,
  onUpdateItemWarehouse,
  onAddLineItem,
  canAddLineItem,
  orderNotes,
  onOrderNotesChange,
  orderComments,
  onOrderCommentsChange,
  headerDropShip,
  onHeaderDropShipChange,
  onSubmitToERP,
  onReset,
  submitError,
  onClearSubmitError,
  customerShipToSearchModal,
  onOpenCustomerShipToSearch,
  onCustomerShipToSearchSelect,
  aiOrderNumber,
  editedPoNumber,
  poNumberWarning,
  onPONumberChange,
  onCloseCustomerShipToSearch,
}) {
  return (
    <div className="h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
            <FileCheck className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-semibold text-white">Order Processor</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={onReset}
            className="text-slate-400 hover:text-white text-sm transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            New Order
          </button>
          {user && (
            <>
              <div className="h-4 w-px bg-slate-600" />
              <div className="flex items-center gap-2 text-slate-300">
                <div className="w-7 h-7 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <span className="text-sm">{user.userDetails}</span>
              </div>
              <a
                href="/.auth/logout?post_logout_redirect_uri=/"
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </a>
            </>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Document Preview */}
        <DocumentPreview
          selectedFile={selectedFile}
          filePreviewUrl={filePreviewUrl}
          previewType={previewType}
          textContent={textContent}
          zoom={zoom}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />

        {/* Right Panel - Extracted Data */}
        <div className="w-1/2 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 bg-slate-800 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-300">Extracted Order Data</span>
            {validationResult?.overall_confidence && (
              <span className={`text-xs px-2 py-1 rounded ${
                validationResult.overall_confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                validationResult.overall_confidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {validationResult.overall_confidence.toUpperCase()} CONFIDENCE
              </span>
            )}
          </div>

          <div className="flex-1 overflow-auto p-4 space-y-4">
            <OrderHeader
              aiOrderNumber={aiOrderNumber}
              extractedData={extractedData}
              validationResult={validationResult}
              selectedCustomer={selectedCustomer}
              requestedShipDate={requestedShipDate}
              onRequestedShipDateChange={onRequestedShipDateChange}
              fobCodes={fobCodes}
              carrierCodes={carrierCodes}
              selectedFobCode={selectedFobCode}
              selectedCarrierCode={selectedCarrierCode}
              onFobCodeChange={onFobCodeChange}
              onCarrierCodeChange={onCarrierCodeChange}
              warehouseCodes={warehouseCodes}
              selectedWarehouse={selectedWarehouse}
              onWarehouseChange={onWarehouseChange}
              shipVia={shipVia}
              onShipViaChange={onShipViaChange}
              orderNotes={orderNotes}
              onOrderNotesChange={onOrderNotesChange}
              headerDropShip={headerDropShip}
              onHeaderDropShipChange={onHeaderDropShipChange}
              customerDropdownOpen={customerDropdownOpen}
              onCustomerDropdownToggle={onCustomerDropdownToggle}
              onSelectCustomer={onSelectCustomer}
              onOpenCustomerShipToSearch={onOpenCustomerShipToSearch}
              editedPoNumber={editedPoNumber}
              poNumberWarning={poNumberWarning}
              onPONumberChange={onPONumberChange}
            />

            <ShipToCard
              validationResult={validationResult}
              selectedShipTo={selectedShipTo}
              selectedCustomer={selectedCustomer}
              shipToDropdownOpen={shipToDropdownOpen}
              onShipToDropdownToggle={onShipToDropdownToggle}
              onSelectShipTo={onSelectShipTo}
              onOpenCustomerShipToSearch={onOpenCustomerShipToSearch}
              salesRep={salesRep}
            />

            <LineItems
              selectedItems={selectedItems}
              itemsRevalidating={itemsRevalidating}
              itemRevalidationFailed={itemRevalidationFailed}
              inventoryLoading={inventoryLoading}
              validationResult={validationResult}
              selectedShipTo={selectedShipTo}
              user={user}
              correlationId={correlationId}
              onUpdateItemSelection={onUpdateItemSelection}
              onConfirmItem={onConfirmItem}
              onUpdateItemQuantity={onUpdateItemQuantity}
              onValidatePrice={onValidatePrice}
              onResolvePrice={onResolvePrice}
              onDeleteLineItem={onDeleteLineItem}
              onUpdateNote={onUpdateNote}
              onUpdateNotePrintCode={onUpdateNotePrintCode}
              onUpdateItemVendor={onUpdateItemVendor}
              onUpdateItemCost={onUpdateItemCost}
              onUpdateItemWarehouse={onUpdateItemWarehouse}
              warehouseCodes={warehouseCodes}
              headerDropShip={headerDropShip}
              warehouseId={selectedWarehouse?.code || null}
              onAddLineItem={onAddLineItem}
              canAddLineItem={canAddLineItem}
              selectedCustomer={selectedCustomer}
            />

            <OrderComments
              orderComments={orderComments}
              onOrderCommentsChange={onOrderCommentsChange}
            />

            <OrderTotals extractedData={extractedData} selectedItems={selectedItems} />

            {/* Order-form products the customer didn't mark (Tonya, 2026-10-12): kept off the order,
                listed here so a missed faint circle can be added back with "Add Line Item". */}
            {extractedData?.skipped_items?.length > 0 && (
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <MinusCircle className="w-3 h-3" />
                  Skipped (not ordered on the PO)
                </h3>
                <ul className="text-sm text-slate-300 space-y-1">
                  {extractedData.skipped_items.map((s, i) => (
                    <li key={i}>
                      <span className="font-mono text-slate-200">{s.item_number || '—'}</span>
                      {s.description && <span className="text-slate-400"> · {s.description}</span>}
                      {s.reason && <span className="text-slate-500"> ({s.reason})</span>}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 mt-2">If one of these was actually ordered, add it with "Add Line Item".</p>
              </div>
            )}

            {extractedData?.notes && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">Special Instructions</h3>
                <p className="text-sm text-slate-300">{extractedData.notes}</p>
              </div>
            )}

            <ValidationIssues unresolvedIssues={unresolvedIssues} bulkPrice={bulkPrice} />
          </div>

          {/* Submission error — shown in place so the CSR keeps their work and can resubmit */}
          {submitError && (
            <div className="px-4 py-3 border-t border-red-500/30 bg-red-500/10 flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-red-300">
                <span className="font-semibold text-red-400">Submission to A+ failed.</span> {submitError}
                <span className="block text-red-300/80 text-xs mt-0.5">Correct the issue and resubmit.</span>
              </div>
              <button
                onClick={onClearSubmitError}
                className="text-red-400/70 hover:text-red-300 transition-colors flex-shrink-0"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-700 bg-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedItems.length === 0 ? (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm">No line items</span>
                </>
              ) : unresolvedCount > 0 ? (
                <button
                  onClick={() => {
                    const el = document.getElementById('validation-issues-panel');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm underline underline-offset-2 decoration-amber-400/40">
                    {unresolvedCount} item{unresolvedCount > 1 ? 's' : ''} need{unresolvedCount === 1 ? 's' : ''} attention
                  </span>
                </button>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 text-sm">All items validated</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!requestedShipDate && (
                <span className="text-amber-400 text-xs">Select a requested ship date</span>
              )}
              <button
                onClick={onReset}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSubmitToERP}
                disabled={selectedItems.length === 0 || unresolvedCount > 0 || !requestedShipDate}
                title={!requestedShipDate ? 'Select a requested ship date' : undefined}
                className={`px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all
                  ${selectedItems.length === 0 || unresolvedCount > 0 || !requestedShipDate
                    ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:shadow-lg hover:shadow-amber-500/20'
                  }`}
              >
                Submit to A+
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {customerShipToSearchModal && (
        <CustomerShipToSearchModal
          initialStep={customerShipToSearchModal.initialStep}
          selectedCustomer={selectedCustomer}
          correlationId={correlationId}
          user={user}
          onSelect={onCustomerShipToSearchSelect}
          onClose={onCloseCustomerShipToSearch}
        />
      )}
    </div>
  );
}
